import { Fab } from "@mui/material";
import React, { useContext } from "react";
import { FiPrinter } from "react-icons/fi";
import * as i18n from "i18next";
import { ISessionState, SessionContext } from "../../../utility/useSession";

import { BookingFabLabel } from "../BookingFab/BookingFabV2";
import styles from "../BookingFab/styles";
import DocumentGenerator from "../BookingView/components/DocumentGenerator";
import propTypes from "prop-types";
import { api, AssetMovement, Booking, BookingHistory, IPreferences, KeyAudit, MovementReasonType, Payment, ProductSaleItemHistory, VehicleAudit } from "../../../logic/api";
import { formatDateTime, promisifyApi } from "../../../utility/utils";
import { Content, ContentStack, ContentText, TDocumentDefinitions } from "pdfmake/interfaces";
import { useFormatter } from "../../../utility/useFormatter";
import { useTranslation } from "react-i18next";
import { useReleaseManagement, ReleaseFlag } from "../../../utility/feature-management";
import { useUserPermissions } from "@/hooks/useUserPermissions";

type SummaryFields = Map<string, string>;

interface ISummarySection {
  leftValues: SummaryFields,
  rightValues: SummaryFields,
}

interface IPrintData {
  bookingHistory?: BookingHistory[],
  movementHistory?: AssetMovement[],
  productHistory?: ProductSaleItemHistory[],
  paymentHistory?: Payment[],
  auditHistory?: (KeyAudit | VehicleAudit)[],
}

interface IFormatters {
  bookingHistoryFormatter: (item: BookingHistory) => string | null;
  assetMovementFormatter: (item: AssetMovement) => string;
  productHistoryFormatter: (item: ProductSaleItemHistory) => string;
  paymentHistoryFormatter: (item: Payment) => string;
  auditHistoryFormatter: (item: KeyAudit | VehicleAudit) => string[];
}

export const bookingDetailsDocumentMaker = (
  session: ISessionState,
  formatters: IFormatters,
  t: typeof i18n.t,
  booking: Booking,
  history: IPrintData,
  isCollectionDeliveryMeetGreetReleased?: boolean,
) => {
  if (session.current.site == null) {
    return "";
  }
  let currentSite = "";

  if (session.current.site.home !== null) {
    if (session.current.credential != null) {
      currentSite = session.current.credential.database;
    }
    currentSite = `${currentSite} - ${session.current.site.home.name}`;
  }

  const htmlToPdfMake = (value: string): ContentText[] => {
    const cleaned = value
      .replaceAll("<b>", "")
      .replaceAll("</b>", "")
      .replaceAll(/<i[^>]*>/g, " - ")
      .replaceAll("</i>", "")
      .replaceAll(/<\d+>/g, "")
      .replaceAll(/<\/\d+>/g, "");

    return [
      { text: cleaned }
    ];
  }

  const buildBookingHistory = (bookingHistory: BookingHistory[] | undefined) => {
    if (!bookingHistory) {
      return [];
    }

    return bookingHistory.map((historyItem) => {
      return {
        date: formatDateTime(historyItem.createDate, session),
        description: htmlToPdfMake(formatters.bookingHistoryFormatter(historyItem) || ""),
      };
    });
  };

  const buildMovementHistory = (movementHistory: AssetMovement[] | undefined) => {
    if (!movementHistory) {
      return [];
    }

    return movementHistory.filter(movement => movement.reason !== MovementReasonType.bookingCreation).map((historyItem) => ({
      date: formatDateTime(historyItem.movementDate, session),
      description: htmlToPdfMake(formatters.assetMovementFormatter(historyItem)),
    }));
  };

  const buildProductHistory = (productHistory: ProductSaleItemHistory[] | undefined) => {
    if (!productHistory) {
      return [];
    }

    return productHistory.map((historyItem) => ({
      date: formatDateTime(historyItem.createDate, session),
      description: htmlToPdfMake(formatters.productHistoryFormatter(historyItem)),
    }));
  };

  const buildPaymentHistory = (paymentHistory: Payment[] | undefined) => {
    if (!paymentHistory) {
      return [];
    }

    return paymentHistory.map((historyItem) => ({
      date: formatDateTime(historyItem.paymentDate, session),
      description: htmlToPdfMake(formatters.paymentHistoryFormatter(historyItem)),
    }));
  };

  const buildAuditHistory = (auditHistory: (KeyAudit | VehicleAudit)[] | undefined) => {
    if (!auditHistory) {
      return [];
    }

    return auditHistory.flatMap(
      (historyItem) => {
        const date = formatDateTime(historyItem.createDate, session);
        const descriptions = formatters.auditHistoryFormatter(historyItem);
        return descriptions.map((description) => ({ date, description: htmlToPdfMake(description) }));
      }
    );
  };

  const buildSummary = (): (ISummarySection | SummaryFields)[] => {
    const preferences = session.current.site?.preferences as IPreferences;

    const bookingDetails: (ISummarySection | SummaryFields)[] = [];

    const referenceSection: ISummarySection = {
      leftValues: new Map<string, string>(),
      rightValues: new Map<string, string>(),
    }
    bookingDetails.push(referenceSection);

    referenceSection.leftValues.set(t("booking.labels.ref") + (":"), booking.ref);
    if (preferences.bookingRecordOptions?.properties.altRef) {
      referenceSection.leftValues.set(t("booking.labels.altRefShort") + (":"), booking.altRef);
    }
    referenceSection.rightValues.set(t("booking.labels.package") + (":"), `${booking.packageCode} / ${booking.packageRef}`);


    const contactVehicleFlightSection: ISummarySection = {
      leftValues: new Map<string, string>(),
      rightValues: new Map<string, string>(),
    };
    bookingDetails.push(contactVehicleFlightSection);

    contactVehicleFlightSection.leftValues.set(t("booking.labels.leadName") + (":"), booking.leadContact.fullName);
    if (preferences.contactRecordOptions?.properties.passengers) {
      contactVehicleFlightSection.rightValues.set(t("booking.labels.pax") + (":"), booking.paxCount.toString());
    }
    contactVehicleFlightSection.leftValues.set(t("booking.labels.registration") + (":"), booking.vehicle.registration);
    contactVehicleFlightSection.rightValues.set(t("booking.labels.flightOutboundNo") + (":"), booking.outboundFlight.flightNumber);
    if (isCollectionDeliveryMeetGreetReleased && preferences.bookingRecordOptions?.properties.confirmedArrivalDate) {
      contactVehicleFlightSection.rightValues.set(t("booking.labels.confirmedArrivalDate") + (":"), formatDateTime(booking.confirmedArrivalDate, session));
    }
    contactVehicleFlightSection.leftValues.set(t("booking.labels.make") + (":"), booking.vehicle.make);
    contactVehicleFlightSection.rightValues.set(t("booking.labels.outboundFlightDate") + (":"), formatDateTime(booking.outboundFlight.flightDate, session));
    if (isCollectionDeliveryMeetGreetReleased && preferences.bookingRecordOptions?.properties.confirmedReturnDate) {
      contactVehicleFlightSection.rightValues.set(t("booking.labels.confirmedReturnDate") + (":"), formatDateTime(booking.confirmedReturnDate, session));
    }
    if (preferences.vehicleRecordOptions?.properties.model) {
      contactVehicleFlightSection.leftValues.set(t("booking.labels.model") + (":"), booking.vehicle.model);
    }
    if (preferences.returnFlightRecordOptions?.properties.flightNumber) {
      contactVehicleFlightSection.rightValues.set(t("booking.labels.flightReturnNo") + (":"), booking.returnFlight.flightNumber);
    }
    contactVehicleFlightSection.leftValues.set(t("booking.labels.colour") + (":"), booking.vehicle.colour);
    contactVehicleFlightSection.rightValues.set(t("booking.labels.returnFlightDate") + (":"), formatDateTime(booking.returnFlight.flightDate, session));
    contactVehicleFlightSection.rightValues.set(t("booking.labels.destination") + (":"), booking.returnFlight.flightDestination == null ? "" : booking.returnFlight.flightDestination);


    const bookingSection: ISummarySection = {
      leftValues: new Map<string, string>(),
      rightValues: new Map<string, string>(),
    };
    bookingDetails.push(bookingSection);

    bookingSection.leftValues.set(t("booking.labels.startDate") + (":"), formatDateTime(booking.bookingStartDate, session));
    bookingSection.rightValues.set(t("booking.labels.endDate") + (":"), formatDateTime(booking.bookingEndDate, session));

    if ((booking as Booking & { showShuttlePickupRequest?: boolean }).showShuttlePickupRequest) {
      bookingSection.leftValues.set(t("settings.shuttlePickup.pickupRequested") + (":"), booking.pickupRequested ? t("general.yes") : t("general.no"));
    }

    if (booking.remarks?.length) {
      const remarks = new Map<string, string>();
      remarks.set(t("booking.labels.remarks") + (":"), booking.remarks[0].remark);
      bookingDetails.push(remarks);
    }

    return bookingDetails;
  };

  const buildDoc = (
    summarySections: (ISummarySection | SummaryFields)[],
    history: IPrintData,
  ): TDocumentDefinitions => {
    const header: Content[] = [
      {
        style: "heading1",
        columns: [
          {
            width: "*",
            text: `${t("booking.labels.bookingDetails")} @ ${formatDateTime(new Date(), session)}`
          },
          {
            width: "*",
            text: currentSite,
            alignment: 'right'
          }
        ]
      }
    ];

    const buildValue = (label: string, value: string) => ({
      columns: [
        {
          style: "data_label",
          width: 100,
          text: label,
        },
        {
          style: "data_value",
          text: value,
        }
      ]
    })

    const buildDataSet = (values: Map<string, string>): ContentStack => ({
      stack: Array
        .from(values.keys())
        .map((k) => buildValue(k, values.get(k) || "")),
    });

    const buildSection = (
      section: ISummarySection | SummaryFields,
    ) => {
      if (section instanceof Map) {
        return {
          ...buildDataSet(section),
          style: "section",
        };
      }
      return {
        columns: [
          buildDataSet(section.leftValues),
          buildDataSet(section.rightValues),
        ],
        style: "section",
      }
    };

    const buildHistorySection = (
      title: string,
      items: {
        date: string,
        description: string | ContentText[],
      }[]
    ) => {
      const content = items.length
        ? {
          style: "table",
          layout: "lightHorizontalLines", // optional
          table: {
            headerRows: 1,
            widths: ["auto", "*"],
            body: [
              [
                { style: "tablehead", text: "Date" },
                { style: "tablehead", text: "Description" },
              ],
              ...items.map((record) => ([
                { style: "tablecell", text: record.date },
                { style: "tablecell", text: record.description },
              ])),
            ],
          },
        }
        : { text: t("input.validations.noHistory") };

      return [
        { style: "heading3", text: title },
        content,
      ]
    }

    return {
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [20, 20, 20, 20],
      content: [
        ...header,
        ...summarySections.map((section) => buildSection(section)),
        { style: "heading2", text: t("booking.labels.history") },
        ...buildHistorySection(t("booking.labels.booking"), buildBookingHistory(history.bookingHistory)),
        ...buildHistorySection(t("booking.labels.assets"), buildMovementHistory(history.movementHistory)),
        ...buildHistorySection(t("booking.labels.products"), buildProductHistory(history.productHistory)),
        ...buildHistorySection(t("booking.labels.payments"), buildPaymentHistory(history.paymentHistory)),
        ...buildHistorySection(t("booking.labels.audit"), buildAuditHistory(history.auditHistory)),
      ],
      styles: {
        heading1: {
          fontSize: 16,
          lineHeight: 1.2,
          bold: true,
        },
        heading2: {
          fontSize: 14,
          bold: true,
          lineHeight: 1.2,
          marginTop: 10,
        },
        heading3: {
          fontSize: 10,
          bold: true,
          lineHeight: 1.2,
          marginTop: 5,
        },
        data_label: {
          fontSize: 10,
          bold: true,
          lineHeight: 1.2,
        },
        data_value: {
          fontSize: 8,
          lineHeight: 1.2,
        },
        footer: {
          fontSize: 10,
          bold: true,
        },
        recordcount: {
          fontSize: 16,
          bold: true,
        },
        timestamp: {
          fontSize: 14,
          bold: false,
        },
        table: {
          marginBottom: 5,
        },
        tablehead: {
          fontSize: 8,
          bold: true,
        },
        tablecell_bold: {
          fontSize: 10,
          bold: true,
        },
        tablecell: {
          fontSize: 8,
        },
        invoiceline: {
          fontSize: 10,
        },
        invoicelinebold: {
          fontSize: 14,
          bold: true,
        },
        address: {
          fontSize: 10,
        },
        section: {
          marginTop: 10,
        }
      },
    };
  }

  return buildDoc(
    buildSummary(),
    history,
  );
}

export const BookingDetailsPrint: React.FunctionComponent<{
  booking: Booking;
  onClick: () => void;
}> = ({
  booking,
  onClick,
}) => {
    const { t } = useTranslation();
    const formatters = useFormatter();
    const session = useContext(SessionContext);
    const { hasPermission } = useUserPermissions();

    const getBookingHistory = promisifyApi(api.hooks.booking.getBookingHistory());
    const getBookingRemarks = promisifyApi(api.hooks.booking.listRemarks());
    const getMovementHistory = promisifyApi(api.hooks.booking.getMovementHistory());
    const getProductHistory = promisifyApi(api.hooks.booking.getProductHistory());
    const getPaymentHistory = promisifyApi(api.hooks.booking.getSalesAndPayments());
    const getAuditHistory = promisifyApi(api.hooks.booking.getAuditHistory());
    const isChangeOrderOfFabMenu = useReleaseManagement(ReleaseFlag.releaseChangeOrderOfFabMenu);
    const isReleaseShuttleBusPickupRequest = useReleaseManagement(ReleaseFlag.releaseShuttleBusPickupRequest);
    const isCollectionDeliveryMeetGreetReleased = useReleaseManagement(ReleaseFlag.releaseCollectionDeliveryMeetGreet);
    const isShuttlePickupEnabled =
      session.current.site?.preferences?.request_shuttle_pickup_enabled === true;
    const showShuttlePickupRequest =
      isReleaseShuttleBusPickupRequest && isShuttlePickupEnabled;
    const canPrintBookingDetails = hasPermission("bookingManagement.booking.printDetails");

    if (!canPrintBookingDetails) {
      return null;
    }

    return <DocumentGenerator
      buttonBuilder={(print) => {

        return <Fab
          style={
            { ...styles.fab, backgroundColor: `${isChangeOrderOfFabMenu ? 'rgb(13, 89, 87)' : 'brown'}` }
          }
          onClick={async () => {
            if (!print) {
              return;
            }

            const context = session.current.context;
            const params = { bookingId: booking.id };
            const bookingHistory = await getBookingHistory.send(context, params);
            const movementHistory = await getMovementHistory.send(context, params);
            const productHistory = await getProductHistory.send(context, params);
            const paymentHistory = await getPaymentHistory.send(context, params);
            const auditHistory = await getAuditHistory.send(context, params);
            const remarks = await getBookingRemarks.send(context, params);

            booking.remarks = remarks || [];
            print({
              bookingHistory,
              movementHistory,
              productHistory,
              paymentHistory: paymentHistory?.payments,
              auditHistory,
            });

            onClick();
          }}
          data-testid="Fab-Print-Booking-Details"
        >

          <BookingFabLabel text={t("booking.labels.printBookingDetails")}><FiPrinter size={26}></FiPrinter></BookingFabLabel>
        </Fab>
      }}
      onRequestSerial={(data) => {
        const bookingForPrint = {
          ...booking,
          showShuttlePickupRequest,
        } as Booking & { showShuttlePickupRequest?: boolean };

        const doc = bookingDetailsDocumentMaker(
          session,
          formatters,
          t,
          bookingForPrint,
          data as unknown as IPrintData,
          isCollectionDeliveryMeetGreetReleased,
        );

        return JSON.stringify(doc);
      }}
    ></DocumentGenerator>
  }
BookingDetailsPrint.propTypes = {
  onClick: propTypes.func.isRequired,
  booking: propTypes.any.isRequired,
}
